import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import { Check, X, ZoomIn, RotateCw } from "lucide-react";

// Square crop + zoom for portrait photos → JPEG data URL (768×768). Keeps the face centred and the avatar tidy everywhere.
export const readDataUrl = (file) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });

export const PHOTO_MAX_MB = 40; // sanity cap only — photos are downscaled in the browser before anything is uploaded

// Any camera photo (3–20 MB, any orientation, HEIC where the browser can decode it) → JPEG data URL with the long edge ≤ maxEdge.
export async function loadPhotoDownscaled(file, maxEdge = 2048) {
  let bmp;
  try { bmp = await createImageBitmap(file, { imageOrientation: "from-image" }); }
  catch { bmp = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file); }); }
  const w = bmp.width, h = bmp.height, k = Math.min(1, maxEdge / Math.max(w, h));
  const c = document.createElement("canvas"); c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  if (bmp.close) bmp.close();
  return c.toDataURL("image/jpeg", 0.92);
}

export function checkPhotoFile(file) {
  if (!file) return null;
  if (!file.type.startsWith("image/")) return "Pilih berkas gambar (JPG, PNG, WebP, HEIC)";
  if (file.size > PHOTO_MAX_MB * 1024 * 1024) return `Foto terlalu besar (maksimal ${PHOTO_MAX_MB} MB)`;
  return null;
}

async function cropToDataUrl(src, area, rotation = 0, size = 768) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const canvas = document.createElement("canvas"); canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  const rad = (rotation * Math.PI) / 180;
  // draw the rotated source onto an intermediate canvas large enough to hold it, then copy the crop area
  const big = document.createElement("canvas");
  const diag = Math.ceil(Math.hypot(img.width, img.height)); big.width = diag; big.height = diag;
  const bctx = big.getContext("2d"); bctx.translate(diag / 2, diag / 2); bctx.rotate(rad); bctx.drawImage(img, -img.width / 2, -img.height / 2);
  const ox = (diag - img.width) / 2, oy = (diag - img.height) / 2;
  ctx.drawImage(big, area.x + ox, area.y + oy, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.9);
}

export function PhotoCropper({ src, onDone, onCancel, title = "Atur potret" }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, px) => setArea(px), []);
  const done = async () => { if (!area) return; setBusy(true); try { onDone(await cropToDataUrl(src, area, rotation)); } finally { setBusy(false); } };
  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="photo-cropper">
      <div className="absolute inset-0 bg-slate-900/70" onClick={onCancel} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-[#E7ECF3] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#E7ECF3] px-5 py-3">
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" aria-label="Batal" data-testid="cropper-cancel"><X size={18} /></button>
        </div>
        <div className="relative h-[360px] bg-slate-900">
          <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={1} cropShape="round" showGrid={false}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="space-y-3 px-5 py-4">
          <label className="flex items-center gap-3 text-xs text-slate-600"><ZoomIn size={14} className="shrink-0" />
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-[#2F6BFF]" data-testid="cropper-zoom" /></label>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setRotation((r) => (r + 90) % 360)} className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100" data-testid="cropper-rotate"><RotateCw size={13} /> Putar</button>
            <p className="text-[11px] text-slate-400">Geser untuk memposisikan wajah di tengah lingkaran.</p>
            <button onClick={done} disabled={busy || !area} className="btn-grad flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-sm disabled:opacity-60" data-testid="cropper-apply"><Check size={14} /> Pakai Foto</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
